import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Tool, TabKey } from '../types';
import { tools } from '../data/tools';
import './ToolGrid.css';

interface ToolGridProps {
    onToolClick: (tool: Tool) => void;
    query: string;
    category: TabKey;
    favorites: string[];
    onToggleFavorite: (toolId: string) => void;
}

/** 心形图标 SVG */
const HeartIcon = ({ filled }: { filled: boolean }) => (
    <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill={filled ? 'currentColor' : 'none'}
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
    </svg>
);

/** 卡片内容组件 */
const CardContent = ({
    tool,
    isFavorited,
    onToggleFavorite,
}: {
    tool: Tool;
    isFavorited: boolean;
    onToggleFavorite: (e: React.MouseEvent) => void;
}) => (
    <>
        <span className="tool-card-icon" style={{ background: tool.color }}>
            {tool.icon}
        </span>
        <span className="tool-card-body">
            <span className="tool-card-title">{tool.title}</span>
            <span className="tool-card-desc">{tool.description}</span>
        </span>
        <span className="tool-card-arrow" aria-hidden="true">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="5" y1="12" x2="19" y2="12" />
                <polyline points="12 5 19 12 12 19" />
            </svg>
        </span>
        {/* 心形收藏按钮 */}
        <span
            className={`tool-card-favorite ${isFavorited ? 'favorited' : ''}`}
            onClick={onToggleFavorite}
            title={isFavorited ? '取消收藏' : '收藏'}
            role="button"
            aria-label={isFavorited ? '取消收藏' : '收藏'}
            aria-pressed={isFavorited}
        >
            <HeartIcon filled={isFavorited} />
        </span>
    </>
);

const ToolGrid = ({ onToolClick, query, category, favorites, onToggleFavorite }: ToolGridProps) => {
    const [orderedTools, setOrderedTools] = useState<Tool[]>(tools);

    // 从 localStorage 恢复排序
    useEffect(() => {
        const savedOrder = localStorage.getItem('toolOrder');
        if (savedOrder) {
            try {
                const orderIds = JSON.parse(savedOrder);
                const ordered = orderIds
                    .map((id: string) => tools.find(t => t.id === id))
                    .filter(Boolean) as Tool[];
                const missing = tools.filter(t => !orderIds.includes(t.id));
                setOrderedTools([...ordered, ...missing]);
            } catch {
                setOrderedTools(tools);
            }
        }
    }, []);

    const normalizedQuery = query.trim().toLowerCase();
    const isSearching = normalizedQuery.length > 0;

    const visibleTools = useMemo(() => {
        // 收藏筛选：只保留已收藏的工具
        if (category === 'favorites') {
            const favTools = orderedTools.filter(t => favorites.includes(t.id));
            if (isSearching) {
                return favTools.filter(tool => {
                    const haystack = [tool.title, tool.description, ...(tool.keywords || [])]
                        .join(' ')
                        .toLowerCase();
                    return haystack.includes(normalizedQuery);
                });
            }
            return favTools;
        }

        const byCategory = isSearching
            ? orderedTools
            : category === 'all'
                ? orderedTools
                : orderedTools.filter(tool => tool.category === category);

        if (!isSearching) return byCategory;
        return byCategory.filter(tool => {
            const haystack = [tool.title, tool.description, ...(tool.keywords || [])]
                .join(' ')
                .toLowerCase();
            return haystack.includes(normalizedQuery);
        });
    }, [orderedTools, normalizedQuery, isSearching, category, favorites]);

    const isDragEnabled = !isSearching && category === 'all';

    // ---- 原生 HTML5 拖拽排序 ----
    const dragSourceRef = useRef<number | null>(null);
    const dropTargetRef = useRef<{ index: number; side: 'before' | 'after' } | null>(null);
    const [dropIndicator, setDropIndicator] = useState<{ index: number; side: 'before' | 'after' } | null>(null);

    /** 计算鼠标在网格中对应的插入位置 */
    const calcDropPosition = useCallback((containerEl: HTMLElement, mouseX: number, mouseY: number) => {
        const cards = Array.from(containerEl.querySelectorAll('.tool-card[draggable]')) as HTMLElement[];
        if (cards.length === 0) return;

        let closestIndex = -1;
        let closestDist = Infinity;

        for (let i = 0; i < cards.length; i++) {
            const cardIndex = Number(cards[i].dataset.index);
            if (cardIndex === dragSourceRef.current) continue;

            const rect = cards[i].getBoundingClientRect();
            const cx = rect.left + rect.width / 2;
            const cy = rect.top + rect.height / 2;
            const dist = Math.hypot(mouseX - cx, mouseY - cy);

            if (dist < closestDist) {
                closestDist = dist;
                closestIndex = cardIndex;
            }
        }

        if (closestIndex >= 0) {
            const targetCard = cards.find(c => Number(c.dataset.index) === closestIndex);
            if (targetCard) {
                const rect = targetCard.getBoundingClientRect();
                const midX = rect.left + rect.width / 2;
                const side: 'before' | 'after' = mouseX < midX ? 'before' : 'after';
                dropTargetRef.current = { index: closestIndex, side };
                setDropIndicator({ index: closestIndex, side });
            }
        }
    }, []);

    /** 执行排序 */
    const applyReorder = useCallback(() => {
        const sourceIndex = dragSourceRef.current;
        const target = dropTargetRef.current;
        dragSourceRef.current = null;
        dropTargetRef.current = null;
        setDropIndicator(null);

        if (sourceIndex === null || target === null) return;
        if (sourceIndex === target.index) return;

        let insertIndex = target.index;
        if (target.side === 'after') {
            insertIndex = target.index + 1;
        }
        if (sourceIndex < insertIndex) {
            insertIndex--;
        }

        if (sourceIndex === insertIndex) return;

        const items = Array.from(orderedTools);
        const [moved] = items.splice(sourceIndex, 1);
        items.splice(insertIndex, 0, moved);
        setOrderedTools(items);
        localStorage.setItem('toolOrder', JSON.stringify(items.map(t => t.id)));
    }, [orderedTools]);

    const handleDragStart = (index: number) => (e: React.DragEvent) => {
        dragSourceRef.current = index;
        e.dataTransfer.effectAllowed = 'move';
        const el = e.currentTarget as HTMLElement;
        requestAnimationFrame(() => el.classList.add('drag-source'));
    };

    const handleDragEnd = () => {
        dragSourceRef.current = null;
        dropTargetRef.current = null;
        setDropIndicator(null);
    };

    const handleGridDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        calcDropPosition(e.currentTarget as HTMLElement, e.clientX, e.clientY);
    };

    const handleGridDrop = (e: React.DragEvent) => {
        e.preventDefault();
        applyReorder();
    };

    const handleCardDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        e.dataTransfer.dropEffect = 'move';
        const gridEl = (e.currentTarget as HTMLElement).closest('.tool-grid') as HTMLElement;
        if (gridEl) {
            calcDropPosition(gridEl, e.clientX, e.clientY);
        }
    };

    const handleCardDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        applyReorder();
    };

    // ---- 渲染 ----

    const getDropClass = (index: number) => {
        if (!dropIndicator || dropIndicator.index !== index) return '';
        return dropIndicator.side === 'before' ? 'drop-target-before' : 'drop-target-after';
    };

    /** 判断是否显示收藏空状态 */
    const isFavoritesEmpty = category === 'favorites' && visibleTools.length === 0 && !isSearching;

    return (
        <section className="tool-grid-section">
            <div className="section-heading">
                <h2 className="section-title">全部工具</h2>
                <p className="section-hint">
                    {isSearching
                        ? `找到 ${visibleTools.length} 个工具`
                        : isDragEnabled
                            ? '按住卡片拖动可调整顺序'
                            : `共 ${visibleTools.length} 个工具`}
                </p>
            </div>

            {isFavoritesEmpty ? (
                <div className="empty-state">
                    <div className="empty-icon">💝</div>
                    <p className="empty-title">还没有收藏工具</p>
                    <p className="empty-desc">去其他标签页逛逛吧</p>
                </div>
            ) : visibleTools.length === 0 ? (
                <div className="empty-state">
                    <div className="empty-icon">🔍</div>
                    <p className="empty-title">没有匹配的工具</p>
                    <p className="empty-desc">换个关键词试试吧</p>
                </div>
            ) : isDragEnabled ? (
                <div
                    className="tool-grid"
                    onDragOver={handleGridDragOver}
                    onDrop={handleGridDrop}
                >
                    {orderedTools.map((tool, index) => (
                        <div
                            key={tool.id}
                            className={`tool-card ${index === dragSourceRef.current ? 'drag-source' : ''} ${getDropClass(index)}`}
                            draggable
                            data-index={index}
                            onDragStart={handleDragStart(index)}
                            onDragEnd={handleDragEnd}
                            onDragOver={handleCardDragOver}
                            onDrop={handleCardDrop}
                            onClick={() => onToolClick(tool)}
                        >
                            <CardContent
                                tool={tool}
                                isFavorited={favorites.includes(tool.id)}
                                onToggleFavorite={(e) => {
                                    e.stopPropagation();
                                    onToggleFavorite(tool.id);
                                }}
                            />
                        </div>
                    ))}
                </div>
            ) : (
                <div className="tool-grid">
                    {visibleTools.map(tool => (
                        <button
                            key={tool.id}
                            className="tool-card"
                            onClick={() => onToolClick(tool)}
                        >
                            <CardContent
                                tool={tool}
                                isFavorited={favorites.includes(tool.id)}
                                onToggleFavorite={(e) => {
                                    e.stopPropagation();
                                    onToggleFavorite(tool.id);
                                }}
                            />
                        </button>
                    ))}
                </div>
            )}
        </section>
    );
};

export default ToolGrid;
